import { useState } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'

const navLinks = [
  { to: '/', label: 'Início' },
  { to: '/quem-somos', label: 'Quem Somos' },
  { to: '/solucoes', label: 'Soluções' },
]

const centralLinks = [
  { to: '/login', label: 'Acessar Painel' },
  { to: '/imprimir-boleto', label: 'Imprimir Boleto' },
  { to: '/senha-mensal', label: 'Senha Mensal' },
  { to: '/baixar-suporte', label: 'Baixar Suporte' },
  { to: '/acesso-contador', label: 'Acesso Contador' },
]

// Páginas com hero escuro: o header flutua sobre o hero como uma pílula de
// vidro translúcida (o hero passa por trás, sem faixa pintada)
export const darkHeaderRoutes = [
  '/',
  '/quem-somos',
  '/solucoes',
  '/imprimir-boleto',
  '/senha-mensal',
  '/baixar-suporte',
  '/agendar-visita',
]

export default function Header() {
  const [menuOpen, setMenuOpen] = useState(false)
  const [centralOpen, setCentralOpen] = useState(false)
  const [mobileCentralOpen, setMobileCentralOpen] = useState(false)

  const { pathname } = useLocation()
  const dark = darkHeaderRoutes.includes(pathname)

  const linkBase = dark ? 'text-blue-100' : 'text-quantum-blue'
  const glass = 'bg-slate-950/40 backdrop-blur-md border border-white/10'

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 ${
        dark ? 'p-3 md:p-4' : 'bg-white shadow-md'
      }`}
    >
      <div
        className={`max-w-7xl mx-auto px-4 flex items-center justify-between ${
          dark ? `h-14 md:h-16 md:px-6 rounded-2xl ${glass}` : 'h-20'
        }`}
      >
        {/* Logo */}
        <Link to="/" onClick={() => setMenuOpen(false)}>
          <img
            src={dark ? '/img/geral/logo-quantum-branca.png' : '/img/geral/LogoQuantum.png'}
            alt="Sistema Quantum"
            className={dark ? 'h-9 md:h-10 w-auto' : 'h-12 w-auto'}
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-6">
          {navLinks.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={({ isActive }) =>
                `text-sm font-medium transition-colors hover:text-quantum-red ${
                  isActive ? 'text-quantum-red' : linkBase
                }`
              }
            >
              {label}
            </NavLink>
          ))}

          {/* Central do Usuário dropdown */}
          <div
            className="relative"
            onMouseEnter={() => setCentralOpen(true)}
            onMouseLeave={() => setCentralOpen(false)}
          >
            <button className={`flex items-center gap-1 text-sm font-medium ${linkBase} hover:text-quantum-red transition-colors`}>
              Central do Usuário
              <i className={`fa-solid fa-chevron-down text-xs transition-transform ${centralOpen ? 'rotate-180' : ''}`} />
            </button>

            {centralOpen && (
              <div className="absolute top-full left-0 w-52 pt-1 z-50">
                <div
                  className={`rounded-lg shadow-xl py-1 ${
                    dark ? `rounded-xl ${glass}` : 'bg-white border border-gray-100'
                  }`}
                >
                  {centralLinks.map(({ to, label }) => (
                    <Link
                      key={to}
                      to={to}
                      className={`block px-4 py-2.5 text-sm ${linkBase} hover:bg-quantum-blue hover:text-white transition-colors`}
                      onClick={() => setCentralOpen(false)}
                    >
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>

          <Link
            to="/agendar-visita"
            className="bg-quantum-red text-white text-sm font-semibold px-5 py-2.5 rounded-lg hover:bg-red-700 transition-colors"
          >
            Agende uma Visita
          </Link>
        </nav>

        {/* Mobile hamburger */}
        <button
          className={`lg:hidden p-2 ${dark ? 'text-white' : 'text-quantum-blue'}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Menu"
        >
          <i className={`fa-solid ${menuOpen ? 'fa-xmark' : 'fa-bars'} text-2xl`} />
        </button>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div
          className={`lg:hidden px-4 pb-4 ${
            dark
              ? `max-w-7xl mx-auto mt-2 rounded-2xl ${glass}`
              : 'bg-white border-t border-gray-100'
          }`}
        >
          {navLinks.map(({ to, label }) => (
            <NavLink
              key={to}
              to={to}
              end={to === '/'}
              className={`block py-3 font-medium hover:text-quantum-red ${linkBase} ${
                dark ? 'border-b border-white/5' : 'border-b border-gray-50'
              }`}
              onClick={() => setMenuOpen(false)}
            >
              {label}
            </NavLink>
          ))}

          {/* Mobile Central dropdown */}
          <button
            className={`flex items-center justify-between w-full py-3 font-medium ${linkBase} ${
              dark ? 'border-b border-white/5' : 'border-b border-gray-50'
            }`}
            onClick={() => setMobileCentralOpen(!mobileCentralOpen)}
          >
            Central do Usuário
            <i className={`fa-solid fa-chevron-down text-xs transition-transform ${mobileCentralOpen ? 'rotate-180' : ''}`} />
          </button>
          {mobileCentralOpen && (
            <div className={`rounded-lg mb-2 ${dark ? 'bg-white/5' : 'bg-gray-50'}`}>
              {centralLinks.map(({ to, label }) => (
                <Link
                  key={to}
                  to={to}
                  className={`block px-4 py-2.5 text-sm ${linkBase} hover:text-quantum-red`}
                  onClick={() => setMenuOpen(false)}
                >
                  {label}
                </Link>
              ))}
            </div>
          )}

          <Link
            to="/agendar-visita"
            className="block mt-3 text-center bg-quantum-red text-white font-semibold px-5 py-3 rounded-lg hover:bg-red-700"
            onClick={() => setMenuOpen(false)}
          >
            Agende uma Visita
          </Link>
        </div>
      )}
    </header>
  )
}
